import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../lib/utils"

const badgeVariants = cva(
  "inline-flex h-6 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium whitespace-nowrap transition-colors duration-fast ease-default focus:outline-none focus:ring-2 focus:ring-ring/50",
  {
    variants: {
      /* Status = soft tint + strong text (readable in dense tables, both themes) */
      variant: {
        default: "border-transparent bg-secondary text-muted-foreground",
        secondary: "border-transparent bg-secondary text-muted-foreground",
        outline: "border-border text-muted-foreground",
        destructive: "border-transparent bg-destructive/10 text-destructive-strong",
        error: "border-transparent bg-destructive/10 text-destructive-strong",
        danger: "border-transparent bg-destructive/10 text-destructive-strong",
        success: "border-transparent bg-success/10 text-success-strong",
        warning: "border-transparent bg-warning/10 text-warning-strong",
        info: "border-transparent bg-info/10 text-info-strong",
        primary: "border-transparent bg-primary/10 text-primary-strong",
        muted: "border-transparent bg-secondary text-muted-foreground",
      },
      dot: {
        true: "pl-2",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      dot: false,
    },
  }
)

/* Dot inside the pill carries the status hue (6px). */
const dotColor: Record<string, string> = {
  default: "bg-muted-foreground",
  primary: "bg-primary",
  secondary: "bg-muted-foreground",
  muted: "bg-muted-foreground",
  outline: "bg-muted-foreground",
  success: "bg-success",
  warning: "bg-warning",
  info: "bg-info",
  destructive: "bg-destructive",
  danger: "bg-destructive",
  error: "bg-destructive",
}

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, dot, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant, dot }), className)} {...props}>
      {dot && (
        <span aria-hidden="true"
          className={cn(
            "size-2 shrink-0 rounded-full",
            dotColor[variant ?? "default"]
          )}
        />
      )}
      {children}
    </span>
  )
}

export { Badge, badgeVariants }
